/* custom-page.js
   - Intro video modal (cooldown + session + skip/sound)
   - Alt image lazy-load (img.alt[data-src])
   - Alternate image swap (hover + touch) using data-alt-src etc.
   - Search + Category filter buttons (category affects ONLY #fullRangeGrid products)
*/

(function () {
  "use strict";

  // ---------------- Helpers ----------------
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const norm = (str) => (str || "").toString().trim().toLowerCase();

  function normalizeText(str) {
    if (!str) return "";
    let s = str.toLowerCase().trim();
    if (s.normalize) s = s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    return s.replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();
  }

  // Basic fuzzy match (fast + forgiving)
  function levenshtein(a, b) {
    const m = a.length,
      n = b.length;
    if (m === 0) return n;
    if (n === 0) return m;

    const dp = new Array(n + 1);
    for (let j = 0; j <= n; j++) dp[j] = j;

    for (let i = 1; i <= m; i++) {
      let prev = dp[0];
      dp[0] = i;
      for (let j = 1; j <= n; j++) {
        const temp = dp[j];
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        dp[j] = Math.min(dp[j] + 1, dp[j - 1] + 1, prev + cost);
        prev = temp;
      }
    }
    return dp[n];
  }

  function fuzzyMatch(query, target) {
    if (!query) return true;
    if (!target) return false;
    if (target.includes(query)) return true;

    const words = target.split(" ");
    let best = Infinity;
    for (const w of words) {
      if (!w) continue;
      const d = levenshtein(query, w);
      if (d < best) best = d;
    }
    if (best <= 2) return true;

    const dFull = levenshtein(query, target);
    return dFull <= 3;
  }

  // ---------------- 1) Intro video modal ----------------
  function initIntroVideoModal() {
    const STORAGE_KEY_LAST = "epVideoLastShown";
    const STORAGE_KEY_SEEN_SESSION = "epVideoSeenSes";
    const COOLDOWN_DAYS = 1;
    const DELAY_MS = 5000;

    const modal = $("#epVideoModal");
    const video = $("#epIntroVideo");
    const closeBtn = $("#epCloseBtn");
    const skipBtn = $("#epSkipBtn");
    const soundBtn = $("#epSoundBtn");

    if (!modal) return; // modal not present on this page

    const VIDEO_SRC =
      "https://cdn.shopify.com/videos/c/o/v/99a7c62a16494129a3d1523aee0c3c25.mp4";

    let videoSrcSet = false;
    let fired = false;
    let timer = null;
    let skipTimer = null;

    const now = () => Date.now();
    const days = (ms) => ms / 86400000;

    function getLastShown() {
      try {
        return parseInt(localStorage.getItem(STORAGE_KEY_LAST) || "0", 10);
      } catch (e) {
        return 0;
      }
    }

    function setLastShown() {
      try {
        localStorage.setItem(STORAGE_KEY_LAST, String(now()));
      } catch (e) {}
      try {
        sessionStorage.setItem(STORAGE_KEY_SEEN_SESSION, "1");
      } catch (e) {}
    }

    function seenThisSession() {
      try {
        return !!sessionStorage.getItem(STORAGE_KEY_SEEN_SESSION);
      } catch (e) {
        return false;
      }
    }

    function withinCooldown() {
      const last = getLastShown();
      return last && days(now() - last) < COOLDOWN_DAYS;
    }

    function qaOverride() {
      return /[?&]showVideo=1\b/.test(window.location.search);
    }

    function setVideoSrcOnce() {
      if (!video || videoSrcSet) return;
      video.src = VIDEO_SRC;
      videoSrcSet = true;
    }

    function revealSkipAfterDelay() {
      if (!skipBtn) return;
      skipBtn.style.display = "none";
      if (skipTimer) clearTimeout(skipTimer);
      skipTimer = setTimeout(() => {
        skipBtn.style.display = "inline-block";
        skipBtn.classList.add("fade-in");
        setTimeout(() => skipBtn.classList.remove("fade-in"), 400);
      }, 2000);
    }

    function ensureCloseVisible() {
      const btn = $("#epCloseBtn");
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      if (r.top < 0 || r.left < 0) {
        btn.style.transform = "translate(-6px, 6px)";
      }
    }

    function openModal() {
      if (fired) return;
      fired = true;

      modal.setAttribute("aria-hidden", "false");
      document.body.classList.add("no-scroll");

      if (video) {
        setVideoSrcOnce();
        try {
          video.load();
          video.muted = true;
          video.setAttribute("muted", "");
          const p = video.play();
          if (p && p.catch) p.catch(() => {});
        } catch (e) {}
      }

      revealSkipAfterDelay();
      ensureCloseVisible();
    }

    function closeModal(persist) {
      modal.setAttribute("aria-hidden", "true");
      document.body.classList.remove("no-scroll");

      if (video) {
        try {
          if (!video.paused) video.pause();
        } catch (e) {}
      }

      if (persist) setLastShown();
      if (skipTimer) {
        clearTimeout(skipTimer);
        skipTimer = null;
      }
    }

    function eligibleToShow() {
      if (qaOverride()) return true;
      if (seenThisSession()) return false;
      if (withinCooldown()) return false;

      const url = location.pathname.toLowerCase();
      if (url.includes("/cart") || url.includes("/checkout")) return false;

      return true;
    }

    // Bind handlers (avoid double-bind)
    if (!modal.dataset.bound) {
      modal.dataset.bound = "1";

      if (soundBtn)
        soundBtn.addEventListener("click", () => {
          if (!video) return;
          video.muted = false;
          video.removeAttribute("muted");
          video.volume = 1;
          const p = video.play();
          if (p && p.catch) p.catch(() => {});
          soundBtn.style.display = "none";
        });

      if (skipBtn) skipBtn.addEventListener("click", () => closeModal(true));
      if (closeBtn) closeBtn.addEventListener("click", () => closeModal(true));

      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") closeModal(true);
      });

      if (video) {
        video.addEventListener("ended", () => closeModal(true));
        video.addEventListener("volumechange", () => {
          if (!video.muted && soundBtn) soundBtn.style.display = "none";
        });
      }
    }

    if (!eligibleToShow()) return;

    timer = setTimeout(() => {
      if (!fired) openModal();
    }, DELAY_MS);
  }

  // ---------------- 2) Alt image lazy load (img.alt[data-src]) ----------------
  function initAltLazyLoad() {
    function loadAltForCard(card) {
      if (!card) return;
      const alt = card.querySelector("img.alt");
      if (alt && !alt.getAttribute("src") && alt.dataset && alt.dataset.src) {
        alt.src = alt.dataset.src;
      }
    }

    // Use capture so it triggers even if something stops bubbling
    document.addEventListener(
      "pointerenter",
      (e) => {
        const card = e.target.closest(".product");
        if (!card) return;
        loadAltForCard(card);
      },
      true
    );

    document.addEventListener(
      "touchstart",
      (e) => {
        const card = e.target.closest(".product");
        if (!card) return;
        loadAltForCard(card);
      },
      { passive: true }
    );
  }

  // ---------------- 3) Alternate image swap (primary img -> hover src) ----------------
  function initAltImageSwap() {
    const cards = $$(
      ".product-card, .product-item, .product, .grid-item, [data-product-card]"
    );
    if (!cards.length) return;

    function getHoverSrc(card) {
      const img = $("img", card);
      if (!img) return null;

      const attrCandidates = [
        "data-alt-src",
        "data-alt",
        "data-hover-src",
        "data-hover",
        "data-secondary-src",
        "data-secondary",
      ];

      // preferred: on image
      for (const a of attrCandidates) {
        const v = img.getAttribute(a);
        if (v && v.trim()) return v.trim();
      }

      // fallback: on card
      for (const a of attrCandidates) {
        const v = card.getAttribute(a);
        if (v && v.trim()) return v.trim();
      }

      // fallback: second image in card
      const imgs = $$("img", card);
      if (imgs.length >= 2) {
        const second = imgs[1];
        return (second && (second.currentSrc || second.src)) || null;
      }

      return null;
    }

    function setImageSrc(img, src) {
      if (!img || !src) return;
      img.src = src;
    }

    cards.forEach((card) => {
      const img = $("img", card);
      if (!img) return;

      const hoverSrc = getHoverSrc(card);
      if (!hoverSrc) return;

      // Store original once
      if (!img.dataset.primarySrc) {
        img.dataset.primarySrc = img.currentSrc || img.src || "";
      }
      img.dataset.hoverSrc = hoverSrc;

      const showHover = () => setImageSrc(img, img.dataset.hoverSrc);
      const showPrimary = () => setImageSrc(img, img.dataset.primarySrc);

      // Avoid double binding
      if (card.dataset.altSwapBound) return;
      card.dataset.altSwapBound = "1";

      // Desktop
      card.addEventListener("mouseenter", showHover);
      card.addEventListener("mouseleave", showPrimary);

      // Mobile
      let touchTimer = null;
      card.addEventListener(
        "touchstart",
        () => {
          touchTimer = window.setTimeout(showHover, 120);
        },
        { passive: true }
      );
      card.addEventListener(
        "touchend",
        () => {
          if (touchTimer) window.clearTimeout(touchTimer);
          showPrimary();
        },
        { passive: true }
      );
      card.addEventListener(
        "touchcancel",
        () => {
          if (touchTimer) window.clearTimeout(touchTimer);
          showPrimary();
        },
        { passive: true }
      );
    });
  }

  // ---------------- 4) Search + Category filters (Full Range only) ----------------
  function initSearchAndFilters() {
    const searchInput = $("#productSearch") || $("#searchInput") || $(".search-input");
    const navBar = $(".nav-bar");
    const body = document.body;

    const allProducts = $$(".product-grid .product");
    if (!allProducts.length) return;

    const fullRangeGrid = $("#fullRangeGrid");
    const filterBtns = $$(".filter-btn");

    let activeCategory = ""; // toggled via buttons

    // Cache search names + normalize categories once
    allProducts.forEach((p) => {
      const nameEl = p.querySelector(".product-name");
      const name = nameEl ? nameEl.textContent : "";
      p.dataset.searchName = normalizeText(name);

      // normalize category (supports data-category, data-categories, data-tags)
      const raw =
        p.getAttribute("data-category") ||
        p.getAttribute("data-categories") ||
        p.getAttribute("data-tags") ||
        "";
      // store "primary" category for exact match buttons (skincare/eyes/etc)
      // If multiple categories are provided, first one is considered primary for exact UI,
      // but matching checks all values.
      const parts = raw
        .split(",")
        .map((x) => norm(x))
        .filter(Boolean);
      p.dataset._cats = parts.join(",");
    });

    function setActiveButtonUI() {
      filterBtns.forEach((btn) => {
        const cat = norm(btn.getAttribute("data-filter") || btn.dataset.filter || btn.textContent);
        const isActive = !!activeCategory && cat === activeCategory;
        btn.classList.toggle("is-active", isActive);
        btn.setAttribute("aria-pressed", isActive ? "true" : "false");
      });
    }

    function passesCategoryFilter(productEl) {
      if (!activeCategory) return true;

      // only full range grid is affected
      if (!fullRangeGrid || !fullRangeGrid.contains(productEl)) return true;

      const cats = (productEl.dataset._cats || "")
        .split(",")
        .map((x) => norm(x))
        .filter(Boolean);

      return cats.includes(activeCategory);
    }

    function applyFilters() {
      const q = searchInput ? normalizeText(searchInput.value || "") : "";

      if (q) body.classList.add("search-results-active");
      else body.classList.remove("search-results-active");

      allProducts.forEach((p) => {
        const name = p.dataset.searchName || "";
        const matchesSearch = !q || fuzzyMatch(q, name);
        const matchesCat = passesCategoryFilter(p);

        p.style.display = matchesSearch && matchesCat ? "" : "none";
      });

      setActiveButtonUI();
    }

    // nav/search UI polish
    if (searchInput && navBar) {
      searchInput.addEventListener("focus", () => navBar.classList.add("search-active"));
      searchInput.addEventListener("blur", () => {
        if (!norm(searchInput.value)) navBar.classList.remove("search-active");
      });
    }

    // Filter buttons toggle
    filterBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        const cat = norm(btn.getAttribute("data-filter") || btn.dataset.filter || btn.textContent);
        activeCategory = activeCategory === cat ? "" : cat;
        applyFilters();
      });
    });

    // Search debounce
    if (searchInput) {
      let t;
      searchInput.addEventListener("input", () => {
        clearTimeout(t);
        t = setTimeout(applyFilters, 80);
      });
    }

    applyFilters();
  }

  // ---------------- Init ----------------
  function init() {
    initIntroVideoModal();
    initAltLazyLoad();
    initAltImageSwap();
    initSearchAndFilters();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
